import { useCallback, useMemo, useRef, useState } from "react";
import Plot from "react-plotly.js";

/**
 * Configuración común de los gráficos de las vistas de calidad.
 *
 * Las dos vistas dibujan lo mismo —series temporales por entidad— pero venían
 * con librerías distintas: una con zoom y selección y la otra sin nada. Acá
 * queda el comportamiento en un solo lugar para que no se vuelvan a separar.
 */

/** Nombre de archivo sin espacios ni acentos, que es lo que espera la descarga. */
function nombreArchivo(partes) {
  return partes
    .filter(Boolean)
    .join("_")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9_-]+/g, "_")
    .toLowerCase();
}

/**
 * Config del modebar: zoom, paneo, selección y **descarga de la imagen**.
 *
 * La descarga estaba explícitamente desactivada (`modeBarButtonsToRemove:
 * ["toImage"]`), así que ninguna de las dos vistas podía bajar el gráfico. Se
 * habilita con nombre de archivo propio y a escala 2 para que la imagen sirva
 * en un informe y no salga pixelada.
 */
export function configGrafico(partes, { alto = 460 } = {}) {
  return {
    responsive: true,
    displayModeBar: true,
    displaylogo: false,
    scrollZoom: false,
    toImageButtonOptions: {
      format: "png",
      filename: nombreArchivo(partes),
      scale: 2,
      height: alto,
    },
  };
}

/**
 * Layout base compartido. Cada vista lo extiende con lo suyo (título del eje,
 * alto, modo de caja), pero el cromo y la leyenda se ven igual en las dos.
 */
export function layoutBase({ unidad = "", alto = 460 } = {}) {
  return {
    margin: { l: 60, r: 50, t: 50, b: 110 },
    hovermode: "x",
    dragmode: "zoom",
    legend: {
      orientation: "h",
      yanchor: "top",
      y: -0.18,
      xanchor: "center",
      x: 0.5,
      bgcolor: "rgba(255,255,255,0.7)",
      bordercolor: "#ddd",
      borderwidth: 1,
      font: { size: 10 },
    },
    xaxis: { tickfont: { size: 10 }, automargin: true },
    yaxis: { tickfont: { size: 10 }, title: unidad, automargin: true },
    paper_bgcolor: "rgba(0,0,0,0)",
    plot_bgcolor: "#fafafa",
    height: alto,
  };
}

const RE_EJE = /^[xy]axis\d*$/;

function limitesDeEjes(gd) {
  const full = gd?._fullLayout;
  if (!full) return null;
  const limites = {};
  Object.keys(full).forEach((nombre) => {
    if (!RE_EJE.test(nombre)) return;
    const eje = full[nombre];
    if (!eje || typeof eje.r2l !== "function") return;
    if (!eje.autorange) return;
    const rango = eje.range;
    if (!Array.isArray(rango) || rango.length !== 2) return;
    const [a, b] = rango;
    const invertido = eje.r2l(a) > eje.r2l(b);
    limites[nombre] = {
      minallowed: invertido ? b : a,
      maxallowed: invertido ? a : b,
    };
  });
  return Object.keys(limites).length ? limites : null;
}

function mismosLimites(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  const ejes = Object.keys(a);
  if (ejes.length !== Object.keys(b).length) return false;
  return ejes.every(
    (eje) =>
      b[eje] &&
      String(a[eje].minallowed) === String(b[eje].minallowed) &&
      String(a[eje].maxallowed) === String(b[eje].maxallowed)
  );
}

function conLimites(layout, limites) {
  if (!limites) return layout;
  const salida = { ...layout };
  Object.keys(limites).forEach((eje) => {
    salida[eje] = { ...(salida[eje] || {}), ...limites[eje] };
  });
  return salida;
}

export function PlotAcotado({ layout, onInitialized, onUpdate, ...resto }) {
  const [limites, setLimites] = useState(null);
  const limitesRef = useRef(null);

  const capturar = useCallback((gd) => {
    const nuevos = limitesDeEjes(gd);
    if (nuevos && !mismosLimites(nuevos, limitesRef.current)) {
      limitesRef.current = nuevos;
      setLimites(nuevos);
    }
  }, []);

  const alIniciar = useCallback(
    (figura, gd) => {
      capturar(gd);
      if (onInitialized) onInitialized(figura, gd);
    },
    [capturar, onInitialized]
  );

  const alActualizar = useCallback(
    (figura, gd) => {
      capturar(gd);
      if (onUpdate) onUpdate(figura, gd);
    },
    [capturar, onUpdate]
  );

  const layoutAcotado = useMemo(() => conLimites(layout, limites), [layout, limites]);

  return (
    <Plot
      {...resto}
      layout={layoutAcotado}
      onInitialized={alIniciar}
      onUpdate={alActualizar}
    />
  );
}
